// SupplierDispatches.jsx
import React, { Component } from "react";
import CartContext from "./CartContext";
import Fuse from "fuse.js";
import Swal from "sweetalert2";
import {
  Navbar,
  Nav,
  Button,
  Row,
  Col,
  Table,
  FormControl,
  Stack,
  ButtonToolbar,
  Form,
  Modal,
  Badge,
  Spinner,
} from "react-bootstrap";
import { BiRefresh } from "react-icons/bi";
import { BlobServiceClient } from "@azure/storage-blob";

export class SupplierDispatches extends Component {
  static contextType = CartContext;

  constructor(props) {
    super(props);
    this.state = {
      loading: false,
      suppliers: [],
      dispatches: [],
      filteredDispatches: [],
      searchQuery: "",
      selectedSupplierId: "",
      selectedStatus: "all", // IMPORTANT: initialize status
      addModalShow: false,
      editItem: null,
      msgboxOpen: false,
      msgboxTitle: "",
      msgboxMessage: "",
    };
    // single hidden file input used for row-level attach; store the target row id in pendingUploadRowId
    this.rowFileInputRef = React.createRef();
    this.pendingUploadRowId = null;
  }

  componentDidMount() {
    this.loadSuppliers().then(() => {
      // if user is supplier, preselect supplierId
      const { isSupplierUser, supplierUserId } = this.getSupplierUserContext();
      if (isSupplierUser && supplierUserId) {
        this.setState(
          { selectedSupplierId: supplierUserId, selectedStatus: "all" },
          () => {
            this.fetchDispatches();
          }
        );
      } else {
        this.fetchDispatches();
      }
    });
  }

  getStoreId() {
    return this.props.storeId || this.context?.storeId || "";
  }

  getSupplierUserContext() {
    const ctx = this.context || {};
    const user = ctx.userData || {};
    const roles = ctx.UserRoles || {};
    const isSupplierUser = user && roles && user.roleId === roles.SUPPLIER;
    const supplierUserId = user ? String(user.storeRoleValue || "") : "";
    return { isSupplierUser, supplierUserId };
  }

  loadSuppliers = async () => {
    const storeId = this.getStoreId();
    if (!storeId) return [];
    try {
      const r = await fetch(
        `${process.env.REACT_APP_API}Supplier/GetSuppliers/${storeId}`,
        { credentials: "same-origin" }
      );
      const data = await r.json();
      const list = Array.isArray(data) ? data : [];
      list.sort((a, b) => (a.name || "").localeCompare(b.name || ""));
      this.setState({ suppliers: list });
      return list;
    } catch (err) {
      console.error("loadSuppliers", err);
      this.setState({ suppliers: [] });
      return [];
    }
  };

  buildUrl() {
    const storeId = this.getStoreId();
    if (!storeId) return null;
    const { selectedSupplierId } = this.state;
    if (selectedSupplierId && selectedSupplierId.trim() !== "") {
      return `${process.env.REACT_APP_API}SupplierDispatches/bysupplier/${storeId}/${selectedSupplierId}`;
    }
    return `${process.env.REACT_APP_API}SupplierDispatches/all/${storeId}`;
  }

  fetchDispatches = async () => {
    const url = this.buildUrl();
    if (!url) {
      this.showMessage("Error", "storeId is required");
      return;
    }
    this.setState({ loading: true });
    try {
      const r = await fetch(url, { credentials: "same-origin" });
      if (!r.ok) throw new Error(`Server returned ${r.status}`);
      const data = await r.json();
      const list = Array.isArray(data) ? data : [];
      list.sort((a, b) => {
        const da = a.created ? new Date(a.created).getTime() : 0;
        const db = b.created ? new Date(b.created).getTime() : 0;
        return db - da;
      });
      this.setState({ dispatches: list, loading: false }, () =>
        this.applyFilters()
      );
    } catch (err) {
      console.error("fetchDispatches", err);
      this.showMessage("Error", "Failed to load dispatches");
      this.setState({ loading: false });
    }
  };

  // status tabs
  handleStatusTab = (statusKey) => {
    // statusKey may be null if same selected; ensure default 'all'
    const key = statusKey || "all";
    this.setState({ selectedStatus: key }, () => this.applyFilters());
  };

  onSearchChange = (q) => {
    this.setState({ searchQuery: q }, () => this.applyFilters());
  };

  handleSupplierFilterChange = (e) => {
    const { isSupplierUser } = this.getSupplierUserContext();
    if (isSupplierUser) return; // locked for supplier users
    const val = e.target.value || "";
    this.setState({ selectedSupplierId: val }, () => this.fetchDispatches());
  };

  applyFilters = () => {
    const { dispatches, searchQuery, selectedStatus } = this.state;
    let results = (dispatches || []).slice();

    if (selectedStatus && selectedStatus !== "all") {
      const sNum = Number(selectedStatus);
      results = results.filter((d) => Number(d.status ?? 0) === sNum);
    }

    if (searchQuery && searchQuery.trim()) {
      try {
        const fuse = new Fuse(results, {
          keys: ["orderNo", "fromAddress", "toAddress", "remarks"],
          threshold: 0.3,
          ignoreLocation: true,
        });
        results = fuse.search(searchQuery).map((r) => r.item);
      } catch {
        const q = searchQuery.trim().toLowerCase();
        results = results.filter(
          (d) =>
            (d.orderNo || "").toLowerCase().includes(q) ||
            (d.fromAddress || "").toLowerCase().includes(q) ||
            (d.toAddress || "").toLowerCase().includes(q) ||
            (d.remarks || "").toLowerCase().includes(q)
        );
      }
    }

    this.setState({ filteredDispatches: results });
  };

  statusCounts = () => {
    const counts = { all: 0, 0: 0, 1: 0, 2: 0, 3: 0 };
    (this.state.dispatches || []).forEach((d) => {
      const s = Number(d.status ?? 0);
      counts.all += 1;
      counts[s] = (counts[s] || 0) + 1;
    });
    return counts;
  };

  statusLabel = (status) => {
    switch (Number(status)) {
      case 0:
        return "Dispatched";
      case 1:
        return "In Transit";
      case 2:
        return "Delivered";
      case 3:
        return "Cancelled";
      default:
        return "Unknown";
    }
  };

  showMessage = (title, msg) =>
    this.setState({ msgboxOpen: true, msgboxTitle: title, msgboxMessage: msg });

  hideMessage = () => this.setState({ msgboxOpen: false });

  onAddClick = () => {
    const { isSupplierUser, supplierUserId } = this.getSupplierUserContext();
    const newItem = {
      id: null,
      created: new Date().toISOString(),
      supplierId: isSupplierUser ? supplierUserId : "",
      orderNo: "",
      dispatchDate: null,
      deliveryDate: null,
      fromAddress: "",
      toAddress: "",
      weight: 0,
      unit: "kg",
      dimension: "",
      valueAmount: 0,
      loadingCharges: 0,
      transportationCharges: 0,
      shipmentCost: 0,
      otherCharges: 0,
      taxAmount: 0,
      remarks: "",
      attachmentFileName: "",
      status: 0,
      statusNote: "",
    };
    this.setState({ editItem: newItem, addModalShow: true });
  };

  onEdit = (item) => {
    const clone = { ...item };
    this.setState({ editItem: clone, addModalShow: true });
  };

  onDelete = (id) => {
    Swal.fire({
      title: "Delete?",
      text: "Delete this dispatch record?",
      showCancelButton: true,
    }).then((r) => {
      if (!r.isConfirmed) return;
      fetch(`${process.env.REACT_APP_API}SupplierDispatches/${id}`, {
        method: "DELETE",
        credentials: "same-origin",
      })
        .then((res) => {
          if (res.ok) {
            this.fetchDispatches();
            Swal.fire("Deleted", "", "success");
          } else {
            Swal.fire("Failed", "Delete failed", "error");
          }
        })
        .catch((err) => {
          console.error("delete", err);
          Swal.fire("Error", "Delete failed", "error");
        });
    });
  };

  onModalSaved = () => {
    this.setState({ addModalShow: false, editItem: null }, () =>
      this.fetchDispatches()
    );
  };

  // ---------- ROW-LEVEL ATTACHMENT HANDLERS ----------

  // trigger hidden file input for a given dispatch row
  onRowAttachClick = (dispatchId) => {
    this.pendingUploadRowId = dispatchId;
    if (this.rowFileInputRef?.current) {
      this.rowFileInputRef.current.value = null;
      this.rowFileInputRef.current.click();
    }
  };

  // called when user selects a file from the hidden file input
  handleRowFileSelected = async (e) => {
    const file = e.target.files?.[0];
    const dispatchId = this.pendingUploadRowId;
    this.pendingUploadRowId = null;
    if (!file || !dispatchId) return;

    Swal.fire({
      title: "Uploading attachment...",
      allowOutsideClick: false,
      allowEscapeKey: false,
      didOpen: () => {
        Swal.showLoading();
      },
    });

    try {
      // upload to blob
      const targetFileName = await this.uploadFileToBlob(dispatchId, file);

      // persist filename to server
      await this.updateAttachmentFileNameOnServer(dispatchId, targetFileName);

      Swal.close();
      Swal.fire("Saved", "Attachment uploaded", "success");
      this.fetchDispatches();
    } catch (err) {
      console.error("row attachment failed", err);
      Swal.close();
      Swal.fire("Error", "Attachment upload failed", "error");
    }
  };

  // upload file to Azure blob under 'dispatches/'
  uploadFileToBlob = async (id, file) => {
    if (!file || !id) throw new Error("Missing id/file");
    const blobService = new BlobServiceClient(
      this.context.store.storageBlobUrl + this.context.store.storageSasToken
    );
    const containerClient = blobService.getContainerClient(
      this.context.store.storageBlobContainerName
    );

    // use naming convention: {id}.{ext}
    const ext = (file.name || "").split(".").pop();
    const targetFileName = `${id}.${ext}`;
    const blobClient = containerClient.getBlockBlobClient(
      "dispatches/" + targetFileName
    );

    const options = { blobHTTPHeaders: { blobContentType: file.type } };
    await blobClient.uploadData(file, options);

    return targetFileName;
  };

  // persist attachmentFileName on server for a given dispatch id
  updateAttachmentFileNameOnServer = async (id, filename) => {
    const res = await fetch(
      `${process.env.REACT_APP_API}SupplierDispatches/UpdateAttachmentFileName/`,
      {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ id, attachmentFileName: filename }),
        credentials: "same-origin",
      }
    );
    if (!res.ok) {
      const t = await res.text().catch(() => "");
      throw new Error(t || `Server returned ${res.status}`);
    }
    return true;
  };

  // delete blob + clear filename via API (row-level delete)
  deleteAttachment = async (dispatchId, filename) => {
    if (!dispatchId || !filename) return;
    // first attempt blob delete (best-effort)
    try {
      const blobService = new BlobServiceClient(
        this.context.store.storageBlobUrl + this.context.store.storageSasToken
      );
      const containerClient = blobService.getContainerClient(
        this.context.store.storageBlobContainerName
      );
      const blobClient = containerClient.getBlockBlobClient(
        "dispatches/" + filename
      );
      await blobClient.deleteIfExists({ deleteSnapshots: "include" });
    } catch (err) {
      console.warn("blob delete error", err);
      // continue to clear DB filename even if blob delete fails
    }

    // clear filename on server
    try {
      const res = await fetch(
        `${process.env.REACT_APP_API}SupplierDispatches/UpdateAttachmentFileName/`,
        {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify({ id: dispatchId, attachmentFileName: "" }),
          credentials: "same-origin",
        }
      );
      if (!res.ok) {
        console.warn("server didn't clear filename", res.status);
      }
      this.fetchDispatches();
    } catch (err) {
      console.error("clear filename API failed", err);
      this.fetchDispatches();
    }
  };

  // helper to open attachment in new tab (uses explicit attachmentUrl if provided otherwise builds URL)
  openAttachment = (row) => {
    const store = this.context.store || {};
    const attachmentUrl =
      row.attachmentUrl ||
      (store.storageBlobUrl || "") +
        (store.storageBlobContainerName || "") +
        "/dispatches/" +
        row.attachmentFileName +
        (store.storageSasToken || "");
    if (!attachmentUrl) {
      Swal.fire("Not found", "No attachment URL available", "info");
      return;
    }
    // ensure relative urls start with slash
    const finalUrl =
      attachmentUrl.startsWith("http") || attachmentUrl.startsWith("/")
        ? attachmentUrl
        : `/${attachmentUrl}`;
    window.open(finalUrl, "_blank", "noopener");
  };

  // ---------- rendering ----------
  renderDispatchRow = (d, idx) => {
    const dispatchDate = d.dispatchDate
      ? new Date(d.dispatchDate).toLocaleDateString()
      : "";
    const deliveryDate = d.deliveryDate
      ? new Date(d.deliveryDate).toLocaleDateString()
      : "";

    const store = this.context.store || {};
    const builtAttachmentUrl =
      (store.storageBlobUrl || "") +
      (store.storageBlobContainerName || "") +
      "/dispatches/" +
      d.attachmentFileName +
      (store.storageSasToken || "");

    return (
      <tr key={d.id || idx}>
        <td>{idx + 1}</td>
        <td>{d.orderNo}</td>
        <td>{dispatchDate}</td>
        <td>{deliveryDate}</td>
        <td>{d.fromAddress}</td>
        <td>{d.toAddress}</td>
        <td className="text-end">
          {d.weight ?? ""} {d.unit}
        </td>
        <td className="text-end">{d.valueAmount ?? ""}</td>
        <td>{d.remarks}</td>
        <td>
          {d.attachmentFileName ? (
            <>
              <a
                href={builtAttachmentUrl}
                target="_blank"
                rel="noreferrer"
                className="me-2"
              >
                Attachment
              </a>
              <Button
                size="sm"
                variant="outline-danger"
                onClick={() =>
                  Swal.fire({
                    title: "Delete attachment?",
                    showCancelButton: true,
                  }).then(
                    (r) =>
                      r.isConfirmed &&
                      this.deleteAttachment(d.id, d.attachmentFileName)
                  )
                }
              >
                X
              </Button>
            </>
          ) : (
            <Button
              size="sm"
              variant="outline-primary"
              onClick={() => this.onRowAttachClick(d.id)}
            >
              Attach
            </Button>
          )}
        </td>
        <td>{this.statusLabel(d.status)}</td>
        <td className="text-end">
          <Button size="sm" variant="warning" onClick={() => this.onEdit(d)}>
            ✎
          </Button>{" "}
          <Button
            size="sm"
            variant="danger"
            onClick={() =>
              Swal.fire({
                title: "Delete?",
                text: "Delete this dispatch record?",
                showCancelButton: true,
              }).then((r) => {
                if (!r.isConfirmed) return;
                this.onDelete(d.id);
              })
            }
          >
            🗑
          </Button>
        </td>
      </tr>
    );
  };

  render() {
    const {
      suppliers,
      filteredDispatches,
      loading,
      selectedSupplierId,
      searchQuery,
      selectedStatus,
    } = this.state;
    const { isSupplierUser } = this.getSupplierUserContext();

    const counts = this.statusCounts();

    return (
      <>
        {/* hidden row-level file input */}
        <input
          style={{ display: "none" }}
          type="file"
          accept=".png,.jpg,.jpeg,.pdf,.docx"
          ref={this.rowFileInputRef}
          onChange={this.handleRowFileSelected}
        />

        {this.state.msgboxOpen && (
          <div className="alert alert-info">
            <b>{this.state.msgboxTitle}</b> — {this.state.msgboxMessage}
            <button className="btn btn-sm btn-link" onClick={this.hideMessage}>
              Close
            </button>
          </div>
        )}

        <Navbar className="shadow position-sticky fixed-top py-3 bg-white">
          <div style={{ display: "flex", alignItems: "center", width: "100%" }}>
            <h5 style={{ margin: 0 }}>
              <b>Supplier Dispatches</b>
            </h5>
            <div
              style={{
                marginLeft: "auto",
                display: "flex",
                gap: 8,
                alignItems: "center",
              }}
            >
              <ButtonToolbar>
                <Button className="mainthemecolor" onClick={this.onAddClick}>
                  + Add
                </Button>{" "}
                &nbsp;
                <Button
                  className="mainthemecolor"
                  onClick={() => this.fetchDispatches()}
                >
                  <BiRefresh /> Refresh
                </Button>
              </ButtonToolbar>
            </div>
          </div>
        </Navbar>

        <div className="p-3" style={{ marginTop: 10 }}>
          <Stack
            direction="horizontal"
            gap={3}
            className="mb-2"
            style={{ flexWrap: "wrap" }}
          >
            <FormControl
              style={{ minWidth: 180, maxWidth: 420 }}
              type="search"
              placeholder="Search by order, address, remarks..."
              value={searchQuery}
              onChange={(e) => this.onSearchChange(e.target.value)}
            />

            <Form.Select
              style={{ minWidth: 160, maxWidth: 320 }}
              value={selectedSupplierId}
              onChange={this.handleSupplierFilterChange}
              disabled={isSupplierUser}
            >
              <option value="">— All Suppliers —</option>
              {suppliers.map((s) => (
                <option key={s.id} value={s.id}>
                  {s.name || s.companyName || s.emailId || s.id}
                </option>
              ))}
            </Form.Select>

            <div className="ms-auto">
              <Button
                size="sm"
                onClick={() =>
                  this.setState(
                    {
                      searchQuery: "",
                      selectedSupplierId: "",
                      selectedStatus: "all",
                    },
                    () => this.applyFilters()
                  )
                }
              >
                Clear Filters
              </Button>
            </div>
          </Stack>

          {/* Status tabs */}
          <Nav
            variant="tabs"
            activeKey={selectedStatus || "all"}
            onSelect={this.handleStatusTab}
            className="mb-2"
          >
            <Nav.Item>
              <Nav.Link eventKey="all">
                All{" "}
                <Badge bg="secondary" className="ms-1">
                  {counts.all}
                </Badge>
              </Nav.Link>
            </Nav.Item>
            <Nav.Item>
              <Nav.Link eventKey="0">
                Dispatched{" "}
                <Badge bg="info" className="ms-1">
                  {counts[0] ?? 0}
                </Badge>
              </Nav.Link>
            </Nav.Item>
            <Nav.Item>
              <Nav.Link eventKey="1">
                In Transit{" "}
                <Badge bg="primary" className="ms-1">
                  {counts[1] ?? 0}
                </Badge>
              </Nav.Link>
            </Nav.Item>
            <Nav.Item>
              <Nav.Link eventKey="2">
                Delivered{" "}
                <Badge bg="success" className="ms-1">
                  {counts[2] ?? 0}
                </Badge>
              </Nav.Link>
            </Nav.Item>
            <Nav.Item>
              <Nav.Link eventKey="3">
                Cancelled{" "}
                <Badge bg="danger" className="ms-1">
                  {counts[3] ?? 0}
                </Badge>
              </Nav.Link>
            </Nav.Item>
          </Nav>

          <div className="d-none d-md-block">
            <Table striped bordered hover className="bg-white small">
              <thead>
                <tr>
                  <th>#</th>
                  <th>Order No</th>
                  <th>Dispatch Date</th>
                  <th>Delivery Date (expected)</th>
                  <th>From</th>
                  <th>To</th>
                  <th>Weight / Unit</th>
                  <th>Value</th>
                  <th>Remarks</th>
                  <th>Attachment</th>
                  <th>Status</th>
                  <th className="text-end">Options</th>
                </tr>
              </thead>
              <tbody>
                {loading && (
                  <tr>
                    <td colSpan={12} className="text-center">
                      <Spinner animation="border" />
                    </td>
                  </tr>
                )}
                {!loading && filteredDispatches.length === 0 && (
                  <tr>
                    <td colSpan={12} className="text-center text-muted">
                      No dispatch records
                    </td>
                  </tr>
                )}
                {!loading &&
                  filteredDispatches.map((d, i) =>
                    this.renderDispatchRow(d, i)
                  )}
              </tbody>
            </Table>
          </div>

          {/* Mobile */}
          <div className="d-block d-md-none">
            {loading && (
              <div className="text-center">
                <Spinner animation="border" />
              </div>
            )}
            {!loading && filteredDispatches.length === 0 && (
              <div className="text-center text-muted">No dispatch records</div>
            )}
            {filteredDispatches.map((d, idx) => (
              <div
                key={d.id || idx}
                className="border rounded p-2 mb-2 bg-white"
              >
                <div>
                  <b>{d.orderNo}</b>{" "}
                  <span className="small text-muted">
                    • {this.statusLabel(d.status)}
                  </span>
                </div>
                <div className="small text-muted">
                  {d.fromAddress} → {d.toAddress}
                </div>
                <div className="mt-1">
                  {d.attachmentFileName ? (
                    <>
                      <a
                        href={
                          d.attachmentUrl ||
                          (this.context.store.storageBlobUrl || "") +
                            (this.context.store.storageBlobContainerName ||
                              "") +
                            "/dispatches/" +
                            d.attachmentFileName +
                            (this.context.store.storageSasToken || "")
                        }
                        target="_blank"
                        rel="noreferrer"
                        className="me-2"
                      >
                        Attachment
                      </a>
                      <Button
                        size="sm"
                        variant="outline-danger"
                        onClick={() =>
                          Swal.fire({
                            title: "Delete attachment?",
                            showCancelButton: true,
                          }).then(
                            (r) =>
                              r.isConfirmed &&
                              this.deleteAttachment(d.id, d.attachmentFileName)
                          )
                        }
                      >
                        X
                      </Button>
                    </>
                  ) : (
                    <Button
                      size="sm"
                      variant="outline-primary"
                      onClick={() => this.onRowAttachClick(d.id)}
                    >
                      Attach
                    </Button>
                  )}
                  &nbsp;
                  <Button
                    size="sm"
                    variant="warning"
                    onClick={() => this.onEdit(d)}
                  >
                    ✎
                  </Button>{" "}
                  <Button
                    size="sm"
                    variant="danger"
                    onClick={() => this.onDelete(d.id)}
                  >
                    🗑
                  </Button>
                </div>
              </div>
            ))}
          </div>
        </div>

        {this.state.addModalShow && (
          <DispatchAddEditModal
            show={this.state.addModalShow}
            item={this.state.editItem}
            suppliers={this.state.suppliers}
            onHide={() =>
              this.setState({ addModalShow: false, editItem: null })
            }
            onSaved={this.onModalSaved}
            allowEditSupplier={!this.getSupplierUserContext().isSupplierUser}
          />
        )}
      </>
    );
  }
}

/* DispatchAddEditModal: Add/Edit form (NO attachment field here anymore) */
export class DispatchAddEditModal extends Component {
  static contextType = CartContext;

  constructor(props) {
    super(props);
    const it = this.props.item || {};
    this.state = {
      item: {
        id: it.id || null,
        created: it.created || new Date().toISOString(),
        supplierId: it.supplierId || "",
        orderNo: it.orderNo || "",
        dispatchDate: it.dispatchDate || "",
        deliveryDate: it.deliveryDate || "",
        fromAddress: it.fromAddress || "",
        toAddress: it.toAddress || "",
        weight: it.weight ?? 0,
        unit: it.unit || "kg",
        dimension: it.dimension || "",
        valueAmount: it.valueAmount ?? 0,
        loadingCharges: it.loadingCharges ?? 0,
        transportationCharges: it.transportationCharges ?? 0,
        shipmentCost: it.shipmentCost ?? 0,
        otherCharges: it.otherCharges ?? 0,
        taxAmount: it.taxAmount ?? 0,
        remarks: it.remarks || "",
        attachmentFileName: it.attachmentFileName || "",
        status: it.status ?? 0,
        statusNote: it.statusNote || "",
      },
      saving: false,
      errorMessage: "",
    };
  }

  componentDidMount() {
    // if supplier user, ensure supplierId is set & readonly in parent - parent passes allowEditSupplier
    const { isSupplierUser, supplierUserId } = this.getSupplierUserContext();
    if (isSupplierUser && supplierUserId) {
      this.setState((s) => ({
        item: { ...s.item, supplierId: supplierUserId },
      }));
    }
  }

  getSupplierUserContext() {
    const ctx = this.context || {};
    const user = ctx.userData || {};
    const roles = ctx.UserRoles || {};
    const isSupplierUser = user && roles && user.roleId === roles.SUPPLIER;
    const supplierUserId = user ? String(user.storeRoleValue || "") : "";
    return { isSupplierUser, supplierUserId };
  }

  handleChange = (e) => {
    const { name, value, type } = e.target;
    const val = type === "number" ? (value === "" ? "" : Number(value)) : value;
    this.setState((prev) => ({ item: { ...prev.item, [name]: val } }));
  };

  handleSubmit = (e) => {
    e.preventDefault();
    const payload = { ...this.state.item };

    if (!payload.supplierId) {
      this.setState({ errorMessage: "Supplier is required" });
      return;
    }
    if (!payload.orderNo || !payload.orderNo.trim()) {
      this.setState({ errorMessage: "OrderNo is required" });
      return;
    }

    this.setState({ saving: true, errorMessage: "" });

    fetch(`${process.env.REACT_APP_API}SupplierDispatches/AddOrUpdate`, {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify(payload),
      credentials: "same-origin",
    })
      .then(async (res) => {
        if (!res.ok) {
          const t = await res.text().catch(() => "");
          throw new Error(t || `Server returned ${res.status}`);
        }
        await res.json();
        this.setState({ saving: false });
        this.props.onSaved();
      })
      .catch((err) => {
        console.error("save dispatch", err);
        this.setState({ saving: false });
        Swal.fire("Error", "Save failed", "error");
      });
  };

  render() {
    const { item, saving, errorMessage } = this.state;
    const suppliers = this.props.suppliers || [];
    const { isSupplierUser } = this.getSupplierUserContext();
    return (
      <Modal {...this.props} size="lg" centered backdrop="static" scrollable>
        <Form onSubmit={this.handleSubmit}>
          <Modal.Header closeButton>
            <Modal.Title>
              {item.id ? "Edit Dispatch" : "Add Dispatch"}
            </Modal.Title>
          </Modal.Header>
          <Modal.Body>
            <Row>
              <Col xs={12}>
                <Form.Group className="mb-2">
                  <Form.Label>Supplier</Form.Label>
                  <Form.Select
                    name="supplierId"
                    value={item.supplierId || ""}
                    onChange={this.handleChange}
                    required
                    disabled={
                      this.props.allowEditSupplier === false || isSupplierUser
                    }
                  >
                    <option value="">— Select Supplier —</option>
                    {suppliers.map((s) => (
                      <option key={s.id} value={s.id}>
                        {s.name || s.companyName || s.emailId || s.id}
                      </option>
                    ))}
                  </Form.Select>
                </Form.Group>
              </Col>
            </Row>

            <Row>
              <Col md={6}>
                <Form.Group className="mb-2">
                  <Form.Label>Order No</Form.Label>
                  <Form.Control
                    name="orderNo"
                    value={item.orderNo}
                    onChange={this.handleChange}
                    required
                  />
                </Form.Group>
              </Col>
              <Col md={3}>
                <Form.Group className="mb-2">
                  <Form.Label>Dispatch Date</Form.Label>
                  <Form.Control
                    type="date"
                    name="dispatchDate"
                    value={
                      item.dispatchDate ? item.dispatchDate.split("T")[0] : ""
                    }
                    onChange={this.handleChange}
                  />
                </Form.Group>
              </Col>
              <Col md={3}>
                <Form.Group className="mb-2">
                  <Form.Label>Delivery Date</Form.Label>
                  <Form.Control
                    type="date"
                    name="deliveryDate"
                    value={
                      item.deliveryDate ? item.deliveryDate.split("T")[0] : ""
                    }
                    onChange={this.handleChange}
                  />
                </Form.Group>
              </Col>
            </Row>

            <Row>
              <Col md={6}>
                <Form.Group className="mb-2">
                  <Form.Label>From Address</Form.Label>
                  <Form.Control
                    name="fromAddress"
                    value={item.fromAddress}
                    onChange={this.handleChange}
                  />
                </Form.Group>
              </Col>
              <Col md={6}>
                <Form.Group className="mb-2">
                  <Form.Label>To Address</Form.Label>
                  <Form.Control
                    name="toAddress"
                    value={item.toAddress}
                    onChange={this.handleChange}
                  />
                </Form.Group>
              </Col>
            </Row>

            <Row>
              <Col md={3}>
                <Form.Group className="mb-2">
                  <Form.Label>Weight</Form.Label>
                  <Form.Control
                    type="number"
                    step="0.01"
                    name="weight"
                    value={item.weight}
                    onChange={this.handleChange}
                  />
                </Form.Group>
              </Col>
              <Col md={3}>
                <Form.Group className="mb-2">
                  <Form.Label>Unit</Form.Label>
                  <Form.Control
                    name="unit"
                    value={item.unit}
                    onChange={this.handleChange}
                  />
                </Form.Group>
              </Col>
              <Col md={3}>
                <Form.Group className="mb-2">
                  <Form.Label>Dimension</Form.Label>
                  <Form.Control
                    name="dimension"
                    value={item.dimension}
                    onChange={this.handleChange}
                  />
                </Form.Group>
              </Col>
              <Col md={3}>
                <Form.Group className="mb-2">
                  <Form.Label>Value Amount</Form.Label>
                  <Form.Control
                    type="number"
                    step="0.01"
                    name="valueAmount"
                    value={item.valueAmount}
                    onChange={this.handleChange}
                  />
                </Form.Group>
              </Col>
            </Row>

            <Row>
              <Col md={4}>
                <Form.Group className="mb-2">
                  <Form.Label>Loading Charges</Form.Label>
                  <Form.Control
                    type="number"
                    step="0.01"
                    name="loadingCharges"
                    value={item.loadingCharges}
                    onChange={this.handleChange}
                  />
                </Form.Group>
              </Col>
              <Col md={4}>
                <Form.Group className="mb-2">
                  <Form.Label>Transportation Charges</Form.Label>
                  <Form.Control
                    type="number"
                    step="0.01"
                    name="transportationCharges"
                    value={item.transportationCharges}
                    onChange={this.handleChange}
                  />
                </Form.Group>
              </Col>
              <Col md={4}>
                <Form.Group className="mb-2">
                  <Form.Label>Shipment Cost</Form.Label>
                  <Form.Control
                    type="number"
                    step="0.01"
                    name="shipmentCost"
                    value={item.shipmentCost}
                    onChange={this.handleChange}
                  />
                </Form.Group>
              </Col>
            </Row>

            <Row>
              <Col md={4}>
                <Form.Group className="mb-2">
                  <Form.Label>Other Charges</Form.Label>
                  <Form.Control
                    type="number"
                    step="0.01"
                    name="otherCharges"
                    value={item.otherCharges}
                    onChange={this.handleChange}
                  />
                </Form.Group>
              </Col>
              <Col md={4}>
                <Form.Group className="mb-2">
                  <Form.Label>Tax Amount</Form.Label>
                  <Form.Control
                    type="number"
                    step="0.01"
                    name="taxAmount"
                    value={item.taxAmount}
                    onChange={this.handleChange}
                  />
                </Form.Group>
              </Col>
              <Col md={4}>
                <Form.Group className="mb-2">
                  <Form.Label>Status</Form.Label>
                  <Form.Select
                    name="status"
                    value={item.status}
                    onChange={this.handleChange}
                    disabled={
                      this.props.allowEditSupplier === false || isSupplierUser
                    }
                  >
                    <option value={0}>Dispatched</option>
                    <option value={1}>In Transit</option>
                    <option value={2}>Delivered</option>
                    <option value={3}>Cancelled</option>
                  </Form.Select>
                </Form.Group>
              </Col>
            </Row>

            <Form.Group className="mb-2">
              <Form.Label>Remarks</Form.Label>
              <Form.Control
                as="textarea"
                rows={5}
                maxLength={499}
                name="remarks"
                value={item.remarks}
                onChange={this.handleChange}
              />
            </Form.Group>

            {errorMessage && <div className="text-danger">{errorMessage}</div>}
          </Modal.Body>

          <Modal.Footer>
            <Button type="submit" variant="primary" disabled={saving}>
              {saving ? "Saving..." : "Save"}
            </Button>
            <Button
              variant="secondary"
              onClick={this.props.onHide}
              disabled={saving}
            >
              Close
            </Button>
          </Modal.Footer>
        </Form>
      </Modal>
    );
  }
}

export default SupplierDispatches;
